import type { AiCallDetail } from "@pax/api-contract/ai/calls/v1alpha1";
import { ENCRYPTED_REASONING_LABEL, type PromptPart } from "@pax/api-contract/ai/shared";
import { useQuery } from "@tanstack/react-query";

type CountTokens = (text: string) => number;

/**
 * OpenAI's GPT models, GPT-6 included, count tokens with the open-source `o200k_base` encoding.
 * Its table is 2 MB, so it loads the first time a prompt is shown.
 */
export function useTokenCounter(): CountTokens | null {
  const { data } = useQuery({
    queryKey: ["tokenizer", "o200k_base"],
    queryFn: async () => {
      const [{ Tiktoken }, { default: ranks }] = await Promise.all([
        import("js-tiktoken/lite"),
        import("js-tiktoken/ranks/o200k_base"),
      ]);
      const encoding = new Tiktoken(ranks);
      return { count: (text: string) => encoding.encode(text).length };
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
  return data?.count ?? null;
}

export interface PartTokens {
  tokens: number;
  /** Counted by subtraction rather than from the part's own text. */
  derived: boolean;
}

/**
 * What each part of a prompt cost in input tokens. Text parts are counted as sent. Encrypted
 * reasoning costs the reasoning tokens of the call that produced it. Tool definitions share what
 * is left of the reported input, since the provider rewrites them before the model reads them;
 * with no tools, what is left is the framing around each message.
 */
export function promptPartTokens(
  parts: readonly PromptPart[],
  count: CountTokens,
  inputTokens: number,
  earlierReasoning: AiCallDetail["earlierReasoning"],
): { parts: PartTokens[]; framing: number } {
  const counted = parts.map((part, index): PartTokens | null => {
    if (part.role === "tools") return null;
    if (part.label === ENCRYPTED_REASONING_LABEL)
      return {
        tokens:
          earlierReasoning.find(earlier => earlier.promptParts === index)?.reasoningTokens ?? 0,
        derived: true,
      };
    return { tokens: count(part.sent), derived: false };
  });
  const left = inputTokens - counted.reduce((sum, part) => sum + (part?.tokens ?? 0), 0);
  const toolWeights = parts.map(part => (part.role === "tools" ? count(part.sent) : 0));
  const toolTotal = toolWeights.reduce((sum, weight) => sum + weight, 0);
  const toolShare = toolTotal > 0 ? Math.max(0, left) : 0;
  return {
    parts: counted.map(
      (part, index) =>
        part ?? {
          tokens: Math.round((toolShare * (toolWeights[index] ?? 0)) / toolTotal),
          derived: true,
        },
    ),
    framing: toolTotal > 0 ? 0 : left,
  };
}

/** OpenAI caches nothing under 1,024 tokens, then in steps of 128. */
export function cacheableTokens(tokens: number): number {
  return tokens < 1024 ? 0 : 1024 + Math.floor((tokens - 1024) / 128) * 128;
}
